/**
 * MobileSignRoute — /sign-mobile?token=… landing page.
 *
 * Bill 2026-05-10 — Phase 1 (v0.14.17 bundle).
 *
 * Mounted in App.tsx BEFORE the Teams auth gate so customers can hit it
 * directly via the tokenized link / QR fallback rendered by the desktop
 * SigningSurfaceDispatcher. This page:
 *
 *   1. Reads `?token=…` from the URL.
 *   2. Validates the token via `client.retail.signing.internal.validateMobileToken`
 *      (sibling backend-core agent). The reply hands us {cartId, signer,
 *      docs, surface, cohortKey}.
 *   3. Renders SigningSurfaceDispatcher in `isTouchOnly` mode, scoped to
 *      drawn-mode docs that weren&apos;t already captured on the desktop session.
 *   4. On all-complete, redirects to /signing-thanks for the same branded
 *      experience SignNow signers see.
 *
 * Plain-language doctrine — every label / banner / error uses customer-
 * facing English.
 *
 * CONTRACT(sibling-backend-core agent):
 *   - POST /v3/signing/internal/validate-mobile-token
 *       body  { token }
 *       reply { success: bool, cartId?, signer?, docs?, surface?, cohortKey?, error? }
 *     Wrapped as `client.retail.signing.internal.validateMobileToken`. Until
 *     that wrapper lands, this route renders a soft-error explaining the
 *     situation without leaking implementation detail to the customer.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import {
  SigningSurfaceDispatcher,
  type SignerInfo,
  type DocInfo,
  type SigningSurface,
} from '@/components/signing/SigningSurfaceDispatcher';

interface ValidatedToken {
  cartId: string;
  signer: SignerInfo;
  docs: ReadonlyArray<DocInfo>;
  surface: SigningSurface;
  cohortKey: string;
  /** Set by the server if the desktop already captured ESIGN consent. */
  esignAlreadyRecorded?: boolean;
}

interface TokenValidationReply {
  success: boolean;
  cartId?: string;
  signer?: SignerInfo;
  docs?: ReadonlyArray<DocInfo>;
  surface?: SigningSurface;
  cohortKey?: string;
  esignAlreadyRecorded?: boolean;
  error?: string;
}

// ── Styles (mobile-first, PCB brand) ──────────────────────────────────────

const screenS: CSSProperties = {
  minHeight: '100vh',
  background: 'var(--surface-bg)',
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif",
  color: 'var(--ink-900)',
  display: 'flex',
  flexDirection: 'column',
};

const headerS: CSSProperties = {
  background: 'var(--topbar-solid)',
  color: 'var(--topbar-fg)',
  padding: '14px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const logoMarkS: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 6,
  background: 'var(--surface-card)',
  color: 'var(--pcb-blue-dark)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 1,
  flexShrink: 0,
};

const titleS: CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
};

const subS: CSSProperties = {
  fontSize: 11,
  opacity: 0.85,
  marginTop: 1,
};

const mainS: CSSProperties = {
  flex: 1,
  padding: '16px 14px 28px',
  maxWidth: 640,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

const noticeS = (tone: 'info' | 'warn' | 'err'): CSSProperties => {
  const palettes: Record<'info' | 'warn' | 'err', { bg: string; fg: string; bd: string }> = {
    info: { bg: 'var(--info-bg)', fg: 'var(--pcb-blue-dark)', bd: 'var(--info-line)' },
    warn: { bg: 'var(--warn-bg)', fg: 'var(--warn-text)', bd: 'var(--warn-line)' },
    err: { bg: 'var(--err-bg)', fg: 'var(--err-text)', bd: 'var(--err-line)' },
  };
  const p = palettes[tone];
  return {
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.bd}`,
    borderRadius: 8,
    padding: '12px 14px',
    fontSize: 14,
    lineHeight: 1.5,
  };
};

const footerS: CSSProperties = {
  textAlign: 'center',
  padding: '12px 16px 18px',
  fontSize: 11,
  color: 'var(--ink-600)',
};

// ── Helpers ──────────────────────────────────────────────────────────────

function readToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('token');
    if (!raw) return null;
    // Keep alnum + dash + dot + underscore; cap length.
    return raw.replace(/[^A-Za-z0-9._-]/g, '').slice(0, 512) || null;
  } catch {
    return null;
  }
}

// ── Component ─────────────────────────────────────────────────────────────

export function MobileSignRoute(): JSX.Element {
  const apiClient = useApiClient();

  const [validating, setValidating] = useState<boolean>(true);
  const [validation, setValidation] = useState<ValidatedToken | null>(null);
  const [softError, setSoftError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const token = readToken();
    if (!token) {
      setValidating(false);
      setSoftError("This signing link is missing some information. Contact your branch officer for a fresh link.");
      return;
    }
    (async () => {
      try {
        // CONTRACT(sibling-backend-core agent): when the typed wrapper
        // ships, replace this with
        //   client.retail.signing.internal.validateMobileToken({ token })
        type ValidateShape = {
          retail?: {
            signing?: {
              internal?: {
                validateMobileToken?: (a: { token: string }) => Promise<TokenValidationReply>;
              };
            };
          };
        };
        const maybeValidate =
          (apiClient as unknown as ValidateShape).retail?.signing?.internal?.validateMobileToken;
        if (typeof maybeValidate !== 'function') {
          if (!cancelled) {
            setValidating(false);
            setSoftError(
              "This signing link couldn't be validated. Contact your branch officer."
            );
          }
          return;
        }
        const reply = await maybeValidate({ token });
        if (cancelled) return;
        if (!reply.success || !reply.cartId || !reply.signer || !reply.docs || !reply.surface || !reply.cohortKey) {
          setSoftError(
            reply.error ??
              "This signing link expired or is no longer valid. Contact your branch officer for a fresh link."
          );
          setValidating(false);
          return;
        }
        setValidation({
          cartId: reply.cartId,
          signer: reply.signer,
          docs: reply.docs,
          surface: reply.surface,
          cohortKey: reply.cohortKey,
          ...(reply.esignAlreadyRecorded !== undefined
            ? { esignAlreadyRecorded: reply.esignAlreadyRecorded }
            : {}),
        });
        setValidating(false);
      } catch (err) {
        if (cancelled) return;
        setSoftError(
          err instanceof Error
            ? "This signing link couldn't be validated right now. Please try again in a moment."
            : "This signing link couldn't be validated. Contact your branch officer."
        );
        setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  function redirectToThanks(): void {
    if (typeof window === 'undefined') return;
    const cartId = validation?.cartId ?? '';
    const name = validation?.signer.displayName ?? '';
    const url = `/signing-thanks?cartId=${encodeURIComponent(cartId)}&name=${encodeURIComponent(name)}`;
    window.location.assign(url);
  }

  return (
    <div style={screenS} role="main" aria-label="Finish signing on your phone">
      <header style={headerS}>
        <span aria-hidden style={logoMarkS}>PCB</span>
        <div>
          <div style={titleS}>Phelps County Bank</div>
          <div style={subS}>Finish signing</div>
        </div>
      </header>

      <main style={mainS}>
        {validating && (
          <div style={noticeS('info')}>Checking your signing link…</div>
        )}

        {softError && (
          <div style={noticeS('err')}>
            {softError}
            <div style={{ marginTop: 8, fontSize: 12 }}>
              Call us at <strong>(573) 458-3700</strong> and we&apos;ll send you
              a new link right away.
            </div>
          </div>
        )}

        {!validating && !softError && validation && (
          <>
            <div style={noticeS('info')}>
              Hi {validation.signer.displayName} — finish your signatures below
              to wrap up your account at Phelps County Bank.
            </div>
            <SigningSurfaceDispatcher
              cartId={validation.cartId}
              signers={[validation.signer]}
              docs={validation.docs}
              surface={validation.surface}
              cohortKey={validation.cohortKey}
              isTouchOnly
              drawnOnly
              skipEsignGate={validation.esignAlreadyRecorded === true}
              onAllComplete={redirectToThanks}
              onCancel={() => {
                // Customer cancel — show a soft notice, do not redirect.
                setSoftError(
                  "Looks like you closed the signing flow. Reopen the link your banker sent if you'd like to finish."
                );
              }}
            />
          </>
        )}
      </main>

      <footer style={footerS}>
        Phelps County Bank · Member FDIC · Equal Housing Lender
      </footer>
    </div>
  );
}

export default MobileSignRoute;
